import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import {
  canRedo as fitCanRedo,
  canUndo as fitCanUndo,
  createHistory,
  redo as fitRedo,
  undo as fitUndo,
} from '@fit-editor/fit-core'
import type {
  Activity,
  EditHistoryState,
  FieldDefsByMesgNum,
  Operation,
  OperationWarning,
  TerrainSource,
  TimeDisplayMode,
} from '@fit-editor/fit-core'

import {
  fieldDefsEntriesToMap,
  fieldDefsMapToEntries,
  loadAllActivityRecords,
  loadSelectedActivityId,
  saveActivityRecord,
  saveSelectedActivityId,
  deleteActivityRecord,
} from '@/lib/persistence-db'
import type { UnitSystem } from '@/lib/format'
import { DEFAULT_MAP_LAYER_PREFERENCES } from '@/lib/map-layers'
import type { MapLayerPreferences } from '@/lib/map-layers'
import { useNotificationStore } from '@/store/notification-store'
import {
  applyToHistoryAsync,
  cancelTerrainLoadAsync,
  currentActivityAsync,
  loadTerrainForHistoryAsync,
  splitActivityAsync,
} from '@/worker/fit-client'
import type { TerrainLoadProgress, TerrainLoadSummary } from '@/worker/terrain-loader'
import { isAbortError } from '@/worker/terrain-loader'

/** Friendly, user-facing label per operation type, for toast titles. */
const OPERATION_LABELS: Record<Operation['type'], string> = {
  crop: 'Crop',
  removeInterval: 'Remove interval',
  removeGpsOnly: 'Remove GPS',
  setGpsConstant: 'Set GPS constant',
  interpolateGps: 'Interpolate GPS',
  repairOutlier: 'Repair GPS point',
  shiftTime: 'Shift time',
  editMetadata: 'Edit metadata',
  merge: 'Merge',
  setElevation: 'Apply terrain elevation',
  moveGpsPoints: 'Move GPS point(s)',
  insertPoint: 'Insert point',
  deleteRecords: 'Delete point(s)',
  editField: 'Edit field',
}

/**
 * Session-only terrain-elevation overlay state for one activity (see
 * `loadTerrainForSelected`). Deliberately *not* part of `LoadedActivity`:
 * it is never written to IndexedDB, never in the persisted UI slice, and
 * never in the edit history -- the overlay is a comparison aid, not an
 * edit. Applying it to the file is a separate, explicit `setElevation`
 * operation.
 */
export interface TerrainOverlayState {
  /** `loading` with a `source` already set means "fetching additional tiles"; the existing overlay stays visible. */
  status: 'idle' | 'loading' | 'loaded' | 'error'
  progress?: TerrainLoadProgress | undefined
  /** Set once a load has succeeded; cleared on error/remove. */
  source?: TerrainSource | undefined
  /** Human-readable description of the data actually used (e.g. "Copernicus GLO-30 (30 m)"). */
  description?: string | undefined
  summary?: TerrainLoadSummary | undefined
  error?: string | undefined
  /** Legend-independent show/hide toggle for the overlay series. */
  visible: boolean
  /** Bumped on every successful load so consumers re-sample the (grown) tile cache. */
  revision: number
}

const IDLE_TERRAIN: TerrainOverlayState = { status: 'idle', visible: true, revision: 0 }

export function selectTerrainState(
  state: ActivityStoreState,
  id: string | undefined,
): TerrainOverlayState {
  return (id != null ? state.terrainByActivityId[id] : undefined) ?? IDLE_TERRAIN
}

let nextTerrainRequestId = 1
/** activity id -> in-flight worker request id, so `cancelTerrainLoad` can abort the right load. */
const terrainRequests = new Map<string, number>()

/** One imported file's full edit history, as held in the app-wide store. */
export interface LoadedActivity {
  readonly id: string
  readonly fileName: string
  readonly importedAt: number
  readonly history: EditHistoryState
  /**
   * Field layouts as observed during the original decode (M7, see
   * `activityToDecoded`'s doc comment in fit-core) -- required to
   * losslessly re-emit preserved-unknown-message content on a FIT
   * re-export. Optional at the type level (defaulting to an empty `Map`
   * wherever it's read) purely to avoid forcing every pre-M7 test
   * fixture across this codebase that builds a `LoadedActivity` literal
   * to supply one; every real code path (`addActivity`, split, and
   * restoring from IndexedDB) always provides a real value. A split
   * segment reuses its parent's map (same source file, same message
   * layouts); a merge keeps the base activity's map unchanged, matching
   * `applyMerge`'s own choice to keep the base's preserved content and
   * drop the other input's (see this milestone's handoff report for why
   * that's flagged as a fit-core gap, not something papered over here).
   */
  readonly fieldDefsByMesgNum?: FieldDefsByMesgNum
}

/**
 * One entry in the app-wide undo/redo history. Every user action that
 * changes the document set (an edit on one activity, a batch edit, removing
 * an activity, importing or splitting files) pushes exactly one entry, so
 * Undo always reverses the most recent action of any kind across all
 * activities.
 *
 * - `edit`: one or more activities each had exactly one operation appended
 *   to their own fit-core edit history (`history.ts`'s op log + cursor).
 *   Undo/redo just moves each listed activity's cursor; the operation
 *   itself stays in that activity's log, so it is never recomputed here.
 * - `remove`: the exact `LoadedActivity` object that was removed, and where
 *   it sat in the sidebar, so undo restores it unchanged (full edit
 *   history included) at the same position.
 * - `add`: activities added by an import or a split. Undo removes them;
 *   redo re-adds the same objects.
 *
 * In-memory only: deliberately not persisted (see `partialize`). After a
 * reload the stacks start empty and Undo/Redo fall back to the selected
 * activity's own persisted edit history.
 */
export type GlobalAction =
  | { readonly kind: 'edit'; readonly activityIds: readonly string[]; readonly label: string }
  | {
      readonly kind: 'remove'
      readonly activity: LoadedActivity
      readonly index: number
      readonly wasSelected: boolean
      readonly wasBatchSelected: boolean
      readonly label: string
    }
  | {
      readonly kind: 'add'
      readonly activities: readonly LoadedActivity[]
      readonly previousSelectedId: string | null
      readonly label: string
    }

/** Oldest entries beyond this are dropped from either stack. */
export const GLOBAL_HISTORY_LIMIT = 100

/** One decoded file handed to `importActivities`. */
export interface ImportedFile {
  fileName: string
  activity: Activity
  fieldDefsByMesgNum?: FieldDefsByMesgNum
}

export type Theme = 'light' | 'dark'

/** Timeline chart layout: all metrics overlaid on one plot, or one stacked panel per metric. */
export type TimelineLayout = 'combined' | 'stacked'

/** Which view fills the main workspace for the selected activity. */
export type WorkspaceView = 'dashboard' | 'fitData'

interface UiPreferences {
  theme: Theme
  timeDisplayMode: TimeDisplayMode
  /** Timeline layout; absent (older persisted prefs / test fixtures) means `'combined'`. */
  timelineLayout?: TimelineLayout
  /**
   * Timeline (bottom panel) height the user dragged/keyed it to, in px.
   * Absent means the automatic default (a fixed layout height, grown to fit
   * stacked panels). Always re-clamped to the current viewport at use time
   * (`lib/timeline-panel-size.ts`), never rewritten on window resize.
   */
  timelineHeightPx?: number
  /** Map basemap/overlay/3D choice. Ids are validated against the layer registry at use time (`resolveMapLayers`). */
  mapLayers: MapLayerPreferences
  /** Main workspace view; absent (older persisted prefs / test fixtures) means `'dashboard'`. */
  workspaceView?: WorkspaceView
  /**
   * Display unit system (km/m/km/h vs mi/ft/mph); absent (older persisted
   * prefs / test fixtures) means `'metric'`. Display only: stored data and
   * every export stay in the FIT file's own (SI) units.
   */
  units?: UnitSystem
  /** Dashboard view: the map fills the whole workspace area (data cards hidden). Absent means not maximised. */
  mapMaximized?: boolean
  /** Dashboard view: stats/file cards collapsed to a one-line summary. Absent means expanded. */
  dashboardCollapsed?: boolean
}

/** A selected time range, in epoch milliseconds (matches ECharts' `time`-axis value domain). */
export interface TimeRangeMs {
  start: number
  end: number
}

export interface ActivityStoreState {
  activities: LoadedActivity[]
  selectedActivityId: string | null
  ui: UiPreferences
  /**
   * Map<->timeline synchronization state (M5). Deliberately decoupled from
   * either component: both the map and the timeline read and write this
   * independently via the store rather than referencing each other
   * directly. Epoch-ms (not `Date`) so it matches ECharts' time-axis
   * value domain and stays trivially comparable/serializable.
   */
  hoveredTimestampMs: number | null
  selectedRangeMs: TimeRangeMs | null
  /**
   * "Pick a point on the map" mode for `setGpsConstant`'s coordinate
   * picker (M6) -- deliberately a separate explicit mode rather than
   * overloading the map's existing hover/click behavior, per the M5
   * map's click handler already doing double duty for hover-clear and
   * GPS-jump popups.
   */
  mapPickMode: boolean
  pickedMapPoint: { lat: number; lon: number } | null
  /**
   * Map point-editing mode (drag/insert/delete GPS points directly on the
   * map). Lives in the store rather than in the map component because the
   * map is remounted while the next replay of an edited history loads --
   * edit mode and the point selection must survive that. Mutually
   * exclusive with `mapPickMode`.
   */
  mapEditMode: boolean
  /** Timestamps (epoch ms) of the GPS points selected in map edit mode. */
  mapEditSelectionMs: number[]
  /** "Pick split point(s) on the timeline" mode (M6) -- same rationale as `mapPickMode`. */
  splitPickMode: boolean
  splitPointsMs: number[]
  /**
   * Ids checked for batch operations (M7) in the sidebar -- deliberately
   * independent of `selectedActivityId` (the single activity shown in
   * the dashboard/map/timeline): a user can tick several activities for
   * a batch edit/export without changing which one they're currently
   * looking at.
   */
  batchSelectedIds: string[]
  /** App-wide undo stack (most recent last) -- see `GlobalAction`. Not persisted. */
  globalUndo: GlobalAction[]
  /** App-wide redo stack (most recent last). Cleared by any new user action. Not persisted. */
  globalRedo: GlobalAction[]
  /**
   * Builds history for one decoded file, selects it, and returns its id.
   * Low-level: does *not* record a global undo entry (the UI import path
   * uses `importActivities`, which records one entry per import batch).
   */
  addActivity: (
    fileName: string,
    activity: Activity,
    fieldDefsByMesgNum?: FieldDefsByMesgNum,
  ) => string
  /**
   * Adds every decoded file from one import as a single undoable step
   * ("Import 2 files"), selects the last one, persists them, and returns
   * their ids in input order.
   */
  importActivities: (files: ImportedFile[]) => string[]
  selectActivity: (id: string | null) => void
  /** Removes an activity (undoable: Undo restores it at the same position). */
  removeActivity: (id: string) => void
  toggleBatchSelected: (id: string) => void
  clearBatchSelection: () => void
  /**
   * Reverses the most recent global action. If the global stack is empty
   * (e.g. right after a reload), falls back to undoing the selected
   * activity's own persisted edit history.
   */
  undo: () => void
  /** Re-applies the most recently undone global action (same fallback rule as `undo`). */
  redo: () => void
  setTheme: (theme: Theme) => void
  toggleTheme: () => void
  setTimeDisplayMode: (mode: TimeDisplayMode) => void
  setTimelineLayout: (layout: TimelineLayout) => void
  /** Sets the user-chosen timeline panel height (rounded px), or clears it (`undefined`) back to the automatic default. */
  setTimelineHeightPx: (heightPx: number | undefined) => void
  setWorkspaceView: (view: WorkspaceView) => void
  setUnits: (units: UnitSystem) => void
  setMapBasemap: (basemapId: string) => void
  setMapOverlay: (overlayId: string, enabled: boolean) => void
  setMapTerrain3d: (enabled: boolean) => void
  setMapMaximized: (maximized: boolean) => void
  setDashboardCollapsed: (collapsed: boolean) => void
  setHoveredTimestampMs: (timestampMs: number | null) => void
  setSelectedRangeMs: (range: TimeRangeMs | null) => void
  setMapPickMode: (enabled: boolean) => void
  setPickedMapPoint: (point: { lat: number; lon: number } | null) => void
  /** Turns map point-editing mode on/off -- see `mapEditMode`. Leaving it clears the selection; entering it ends `mapPickMode`. */
  setMapEditMode: (enabled: boolean) => void
  setMapEditSelectionMs: (timestampsMs: number[]) => void
  setSplitPickMode: (enabled: boolean) => void
  addSplitPoint: (timestampMs: number) => void
  removeSplitPoint: (timestampMs: number) => void
  clearSplitPoints: () => void
  /**
   * Applies `operation` to the selected activity's history off the main
   * thread (via the Worker), commits it, and surfaces any
   * `OperationWarning`s as a toast rather than swallowing them. Returns
   * the warnings so a caller (e.g. a confirming dialog) can also react
   * synchronously if it wants to.
   *
   * Returns `null` if the Worker call itself rejected (as opposed to the
   * operation "succeeding with caveats", which is what `OperationWarning`s
   * already cover) -- a toast is fired here either way, but `null` lets a
   * caller distinguish "closed because it worked" from "should stay open,
   * the user's input wasn't lost to a failure they'd otherwise have no way
   * to tell happened" (see every edit dialog's `handleConfirm`).
   */
  applyOperationToSelected: (
    operation: Operation,
    options?: ApplyOperationOptions,
  ) => Promise<OperationWarning[] | null>
  /**
   * The batch-processing equivalent of `applyOperationToSelected` (M7):
   * applies the same `operation` to each of `ids`' histories
   * independently (one activity's warnings -- or failure -- never affects
   * another's), and returns every *successful* id's warnings keyed by id
   * so a caller can report per-activity results. A failed id is simply
   * absent from the returned record; one combined toast at the end
   * summarizes successes/warnings/failures across the whole batch rather
   * than firing `ids.length` separate toasts, and a failure partway
   * through never aborts the remaining ids.
   */
  applyOperationToIds: (
    ids: string[],
    operation: Operation,
  ) => Promise<Record<string, OperationWarning[]>>
  /**
   * Splits the selected activity at `atTimestamps` and adds each
   * resulting segment to the store as its own new, independently
   * editable `LoadedActivity` -- `splitActivity` produces independent
   * activities, not a history entry, so this does not touch the
   * original activity's history at all. Returns the new activities' ids,
   * or `null` if the Worker call rejected (a toast is fired either way;
   * `null` lets `SplitDialog` keep itself open with the user's split
   * points intact rather than discarding them on an unreported failure).
   */
  splitSelectedAtTimestamps: (atTimestamps: Date[]) => Promise<string[] | null>
  /**
   * Restores previously loaded activities (and their full edit history)
   * from IndexedDB (M7) -- called once on app startup. No-ops if
   * activities are already loaded (defends against a double-invoke, e.g.
   * React StrictMode's dev-mode double effect) or if persistence isn't
   * available in this environment (`isPersistenceAvailable()` in
   * `lib/persistence-db.ts`).
   */
  hydrateFromPersistence: () => Promise<void>
  /** Session-only terrain overlay state, keyed by activity id -- see `TerrainOverlayState`. */
  terrainByActivityId: Record<string, TerrainOverlayState>
  /**
   * Fetches terrain tiles for the selected activity's current route (only
   * tile z/x/y requests leave the browser) and marks its overlay loaded.
   * Network access: only call from an explicit user action that has shown
   * the disclosure. Already-cached tiles are not refetched, so calling this
   * again after an edit only loads the missing ones.
   */
  loadTerrainForSelected: () => Promise<void>
  cancelTerrainLoad: (id: string) => void
  setTerrainVisible: (id: string, visible: boolean) => void
  removeTerrain: (id: string) => void
}

function createId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `activity-${Date.now()}-${Math.random().toString(36).slice(2)}`
}

function prefersDarkColorScheme(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
  } catch {
    return false
  }
}

/**
 * All map/timeline sync + pick-mode state that means nothing once the
 * selected activity changes (or a new one is added) -- spread into every
 * state transition that changes `selectedActivityId`.
 */
type SyncState = Pick<
  ActivityStoreState,
  | 'hoveredTimestampMs'
  | 'selectedRangeMs'
  | 'mapPickMode'
  | 'pickedMapPoint'
  | 'splitPickMode'
  | 'splitPointsMs'
  | 'mapEditMode'
  | 'mapEditSelectionMs'
>

function resetSyncState(): SyncState {
  return {
    hoveredTimestampMs: null,
    selectedRangeMs: null,
    mapPickMode: false,
    pickedMapPoint: null,
    splitPickMode: false,
    splitPointsMs: [],
    mapEditMode: false,
    mapEditSelectionMs: [],
  }
}

/** Options for `applyOperationToSelected`. */
export interface ApplyOperationOptions {
  /** Replaces the default "<label> applied" title of the success/warning toast (the warnings themselves are still shown). */
  successTitle?: string
  /**
   * The operation's warnings are informational notes rather than caveats
   * (e.g. `editField`'s "Changed lap #2 totalDistance from X to Y"): show
   * them as the description of a *success* toast instead of a warning one.
   */
  warningsAreNotes?: boolean
  /**
   * Called with the failure message when the operation is rejected (the
   * error toast is still shown) -- lets a caller such as an inline editor
   * show the reason next to the user's input as well.
   */
  onError?: (message: string) => void
}

function notifyOperationResult(
  operation: Operation,
  warnings: OperationWarning[],
  successTitle?: string,
  warningsAreNotes = false,
): void {
  const label = OPERATION_LABELS[operation.type]
  if (warnings.length === 0 || warningsAreNotes) {
    useNotificationStore.getState().notify({
      variant: 'success',
      title: successTitle ?? `${label} applied`,
      ...(warnings.length > 0 ? { description: warnings.map((w) => w.message).join(' ') } : {}),
    })
    return
  }
  useNotificationStore.getState().notify({
    variant: 'warning',
    title:
      successTitle ??
      `${label} applied with ${warnings.length === 1 ? 'a note' : `${warnings.length} notes`}`,
    description: warnings.map((w) => w.message).join(' '),
  })
}

/**
 * Fire-and-forget persistence of one activity's full state to IndexedDB
 * (M7) -- never awaited by its callers, since "survive a reload" must
 * never add latency to an edit the user is actively making. A write
 * failure (quota exceeded, a browser that blocks IndexedDB entirely,
 * etc.) is surfaced as a toast rather than silently swallowed -- it
 * doesn't affect the in-memory session at all, but the user should know
 * their work may not survive a reload.
 */
async function persistActivity(loaded: LoadedActivity): Promise<void> {
  try {
    await saveActivityRecord({
      id: loaded.id,
      fileName: loaded.fileName,
      importedAt: loaded.importedAt,
      original: loaded.history.original,
      log: [...loaded.history.log],
      cursor: loaded.history.cursor,
      fieldDefsEntries: fieldDefsMapToEntries(loaded.fieldDefsByMesgNum ?? new Map()),
    })
  } catch (error) {
    useNotificationStore.getState().notify({
      variant: 'warning',
      title: `Could not save "${loaded.fileName}" for offline persistence`,
      description: error instanceof Error ? error.message : String(error),
    })
  }
}

async function persistSelection(id: string | null): Promise<void> {
  try {
    await saveSelectedActivityId(id)
  } catch {
    // Losing the *remembered selection* across a reload is a minor
    // inconvenience (the app still falls back to selecting the
    // most-recently-restored activity), not a data-loss risk -- unlike
    // `persistActivity`'s failures, this one doesn't warrant a toast on
    // every selection change.
  }
}

type SetState = (
  partial:
    Partial<ActivityStoreState> | ((state: ActivityStoreState) => Partial<ActivityStoreState>),
) => void
type GetState = () => ActivityStoreState

/** The result of `applyOperationToOneId` -- see its own doc comment for why a rejected Worker call is reported this way rather than left to propagate. */
interface ApplyOperationOutcome {
  warnings: OperationWarning[]
  /** Set (to a human-readable message) when the Worker call itself rejected. `warnings` is always `[]` in that case -- nothing was actually applied. */
  error?: string
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

function withoutKey<T>(record: Record<string, T>, key: string): Record<string, T> {
  if (!(key in record)) return record
  const { [key]: _removed, ...rest } = record
  return rest
}

/**
 * Shared by `applyOperationToSelected` and `applyOperationToIds` (M7's
 * batch processing) -- applies `operation` to exactly one activity's
 * history, by id, persists the result, and returns exactly the new
 * operation's warnings (not every warning the activity has ever
 * accumulated). Pulled out to a standalone function (rather than a
 * closure inside `create(...)`) so both callers share one implementation
 * with no duplicated replay-diffing logic.
 *
 * Never lets a rejected Worker call propagate as a thrown exception --
 * `fit-core`'s `applyOperation`/`applyToHistory`/`currentActivity` are
 * currently designed not to throw, but a genuine Worker-communication
 * failure (the thread crashed, a transferable couldn't be cloned, etc.)
 * is still possible and, before this fix, left every caller's `await`
 * rejecting with nothing to show the user (see this milestone's handoff
 * report). Catching here, once, means neither caller has to.
 */
async function applyOperationToOneId(
  set: SetState,
  get: GetState,
  id: string,
  operation: Operation,
): Promise<ApplyOperationOutcome> {
  const loaded = get().activities.find((a) => a.id === id)
  if (loaded == null) return { warnings: [] }

  let newHistory: EditHistoryState
  let newWarnings: OperationWarning[]
  try {
    // `applyToHistoryAsync` only appends to the log (cheap, no replay) --
    // it doesn't itself return the new operation's warnings. Replaying
    // before and after (both off the main thread) and slicing off the
    // already-known prefix isolates exactly the warnings this one new
    // operation introduced, without re-showing every past operation's
    // warnings on every subsequent edit.
    const [{ warnings: priorWarnings }, history] = await Promise.all([
      currentActivityAsync(loaded.history),
      applyToHistoryAsync(loaded.history, operation),
    ])
    const { warnings: allWarnings } = await currentActivityAsync(history)
    newHistory = history
    newWarnings = allWarnings.slice(priorWarnings.length)
  } catch (error) {
    return { warnings: [], error: errorMessage(error) }
  }

  // The Worker round-trip is async: if the activity was removed or its
  // history moved (e.g. an Undo landed) while it ran, `newHistory` was
  // built from a stale base -- committing it would silently discard that
  // intervening change. Report it instead of applying.
  const current = get().activities.find((a) => a.id === id)
  if (current == null || current.history !== loaded.history) {
    return {
      warnings: [],
      error: 'The activity changed while the operation was running; nothing was applied.',
    }
  }

  set((state) => ({
    activities: state.activities.map((a) => (a.id === id ? { ...a, history: newHistory } : a)),
  }))
  const updated = get().activities.find((a) => a.id === id)
  if (updated != null) void persistActivity(updated)
  return { warnings: newWarnings }
}

/** Appends `entry` to `stack`, dropping the oldest entries beyond `GLOBAL_HISTORY_LIMIT`. */
function pushCapped(stack: readonly GlobalAction[], entry: GlobalAction): GlobalAction[] {
  const next = [...stack, entry]
  return next.length > GLOBAL_HISTORY_LIMIT ? next.slice(next.length - GLOBAL_HISTORY_LIMIT) : next
}

function pluralActivities(count: number): string {
  return count === 1 ? '1 activity' : `${count} activities`
}

/** Label for the per-activity fallback (global stack empty): the op the selected activity's cursor would cross. */
function fallbackLabel(
  loaded: LoadedActivity | undefined,
  direction: 'undo' | 'redo',
): string | null {
  if (loaded == null) return null
  const { log, cursor } = loaded.history
  const operation = direction === 'undo' ? log[cursor - 1] : log[cursor]
  if (operation == null) return null
  return `${OPERATION_LABELS[operation.type]} on ${loaded.fileName}`
}

export const useActivityStore = create<ActivityStoreState>()(
  persist(
    (set, get) => {
      /** Records a new user action: pushes it onto the undo stack and clears redo. */
      const recordAction = (entry: GlobalAction): void => {
        set((state) => ({ globalUndo: pushCapped(state.globalUndo, entry), globalRedo: [] }))
      }

      /** Selects `id` if it isn't already, resetting sync state like `selectActivity` does. */
      const selectIfChanged = (id: string | null): void => {
        if (get().selectedActivityId === id) return
        set({ selectedActivityId: id, ...resetSyncState() })
        void persistSelection(id)
      }

      /**
       * Removes one activity without touching the global stacks (shared by
       * `removeActivity`, undo of an `add`, and redo of a `remove`).
       * Returns what a `remove` entry needs to restore it, or `null` if it
       * wasn't loaded.
       */
      const removeActivityInternal = (
        id: string,
      ): Omit<Extract<GlobalAction, { kind: 'remove' }>, 'kind' | 'label'> | null => {
        const state = get()
        const index = state.activities.findIndex((a) => a.id === id)
        const activity = state.activities[index]
        if (activity == null) return null
        const wasSelected = state.selectedActivityId === id
        const wasBatchSelected = state.batchSelectedIds.includes(id)

        // Cancel any in-flight terrain load and forget its request id, so a
        // late result can never write back if this activity is restored by
        // an Undo while the (now aborted) load is still settling.
        get().removeTerrain(id)
        set((s) => {
          const activities = s.activities.filter((a) => a.id !== id)
          return {
            activities,
            selectedActivityId: wasSelected
              ? (activities[activities.length - 1]?.id ?? null)
              : s.selectedActivityId,
            batchSelectedIds: s.batchSelectedIds.filter((batchId) => batchId !== id),
            ...(wasSelected ? resetSyncState() : {}),
          }
        })
        void deleteActivityRecord(id)
        void persistSelection(get().selectedActivityId)
        return { activity, index, wasSelected, wasBatchSelected }
      }

      /** Appends already-built activities (new or restored), persists them, and selects `selectId`. */
      const appendActivities = (
        added: readonly LoadedActivity[],
        selectId: string | null,
      ): void => {
        set((state) => ({
          activities: [
            ...state.activities,
            ...added.filter((a) => !state.activities.some((existing) => existing.id === a.id)),
          ],
        }))
        for (const loaded of added) void persistActivity(loaded)
        set({ selectedActivityId: selectId, ...resetSyncState() })
        void persistSelection(selectId)
      }

      /** Moves each listed activity's own history cursor one step; returns the ids that still exist. */
      const stepEdits = (ids: readonly string[], direction: 'undo' | 'redo'): string[] => {
        const existing = ids.filter((id) => get().activities.some((a) => a.id === id))
        set((state) => ({
          activities: state.activities.map((a) =>
            existing.includes(a.id)
              ? { ...a, history: direction === 'undo' ? fitUndo(a.history) : fitRedo(a.history) }
              : a,
          ),
        }))
        for (const id of existing) {
          const updated = get().activities.find((a) => a.id === id)
          if (updated != null) void persistActivity(updated)
        }
        // Show the change: keep the current selection if it was one of the
        // affected activities, otherwise jump to the first affected one.
        const selected = get().selectedActivityId
        if (existing.length > 0 && (selected == null || !existing.includes(selected))) {
          selectIfChanged(existing[0] ?? null)
        }
        return existing
      }

      /**
       * Performs `entry` in `direction` and returns the entry to push onto
       * the opposite stack. Entries for `add`/`remove` are rebuilt from the
       * *current* objects, so any per-activity edit-log changes made since
       * (e.g. an edit that was later undone, leaving a redoable op in the
       * log) travel with them.
       */
      const performAction = (entry: GlobalAction, direction: 'undo' | 'redo'): GlobalAction => {
        switch (entry.kind) {
          case 'edit':
            stepEdits(entry.activityIds, direction)
            return entry
          case 'remove': {
            if (direction === 'redo') {
              const removed = removeActivityInternal(entry.activity.id)
              return removed == null ? entry : { kind: 'remove', label: entry.label, ...removed }
            }
            const { activity, index, wasBatchSelected } = entry
            set((state) => {
              if (state.activities.some((a) => a.id === activity.id)) return {}
              const activities = [...state.activities]
              activities.splice(Math.min(index, activities.length), 0, activity)
              return {
                activities,
                batchSelectedIds:
                  wasBatchSelected && !state.batchSelectedIds.includes(activity.id)
                    ? [...state.batchSelectedIds, activity.id]
                    : state.batchSelectedIds,
              }
            })
            void persistActivity(activity)
            selectIfChanged(activity.id)
            return entry
          }
          case 'add': {
            if (direction === 'redo') {
              const previousSelectedId = get().selectedActivityId
              appendActivities(entry.activities, entry.activities[0]?.id ?? previousSelectedId)
              return { ...entry, previousSelectedId }
            }
            const current = entry.activities.map(
              (added) => get().activities.find((a) => a.id === added.id) ?? added,
            )
            for (const added of current) removeActivityInternal(added.id)
            const { previousSelectedId } = entry
            const restore =
              previousSelectedId != null &&
              get().activities.some((a) => a.id === previousSelectedId)
                ? previousSelectedId
                : (get().activities[get().activities.length - 1]?.id ?? null)
            selectIfChanged(restore)
            return { ...entry, activities: current }
          }
        }
      }

      /** Per-activity fallback when the global stack in `direction` is empty (e.g. after a reload). */
      const fallbackStep = (direction: 'undo' | 'redo'): GlobalAction | null => {
        const loaded = selectSelectedActivity(get())
        if (loaded == null) return null
        const possible =
          direction === 'undo' ? fitCanUndo(loaded.history) : fitCanRedo(loaded.history)
        const label = fallbackLabel(loaded, direction)
        if (!possible || label == null) return null
        stepEdits([loaded.id], direction)
        return { kind: 'edit', activityIds: [loaded.id], label }
      }

      return {
        activities: [],
        selectedActivityId: null,
        ui: {
          theme: prefersDarkColorScheme() ? 'dark' : 'light',
          timeDisplayMode: 'local',
          timelineLayout: 'combined',
          workspaceView: 'dashboard',
          units: 'metric',
          mapLayers: DEFAULT_MAP_LAYER_PREFERENCES,
        },
        hoveredTimestampMs: null,
        selectedRangeMs: null,
        mapPickMode: false,
        pickedMapPoint: null,
        splitPickMode: false,
        splitPointsMs: [],
        mapEditMode: false,
        mapEditSelectionMs: [],
        batchSelectedIds: [],
        globalUndo: [],
        globalRedo: [],
        terrainByActivityId: {},

        addActivity: (fileName, activity, fieldDefsByMesgNum) => {
          const id = createId()
          const loaded: LoadedActivity = {
            id,
            fileName,
            importedAt: Date.now(),
            history: createHistory(activity),
            fieldDefsByMesgNum: fieldDefsByMesgNum ?? new Map(),
          }
          set((state) => ({
            activities: [...state.activities, loaded],
            selectedActivityId: id,
            ...resetSyncState(),
          }))
          void persistActivity(loaded)
          void persistSelection(id)
          return id
        },

        importActivities: (files) => {
          if (files.length === 0) return []
          const previousSelectedId = get().selectedActivityId
          const added: LoadedActivity[] = files.map(
            ({ fileName, activity, fieldDefsByMesgNum }) => ({
              id: createId(),
              fileName,
              importedAt: Date.now(),
              history: createHistory(activity),
              fieldDefsByMesgNum: fieldDefsByMesgNum ?? new Map(),
            }),
          )
          appendActivities(added, added[added.length - 1]?.id ?? null)
          const first = added[0]
          recordAction({
            kind: 'add',
            activities: added,
            previousSelectedId,
            label:
              added.length === 1 && first != null
                ? `Import ${first.fileName}`
                : `Import ${added.length} files`,
          })
          return added.map((a) => a.id)
        },

        // Switching the selected activity invalidates any map/timeline sync
        // state from the previous one -- a hovered timestamp or selected
        // range from activity A means nothing once activity B is showing.
        selectActivity: (id) => {
          set({ selectedActivityId: id, ...resetSyncState() })
          void persistSelection(id)
        },

        removeActivity: (id) => {
          const removed = removeActivityInternal(id)
          if (removed == null) return
          const { fileName } = removed.activity
          recordAction({ kind: 'remove', label: `Remove activity ${fileName}`, ...removed })
          useNotificationStore.getState().notify({
            variant: 'success',
            title: `Removed ${fileName} — press Undo (Ctrl+Z) to restore`,
          })
        },

        toggleBatchSelected: (id) =>
          set((state) => ({
            batchSelectedIds: state.batchSelectedIds.includes(id)
              ? state.batchSelectedIds.filter((batchId) => batchId !== id)
              : [...state.batchSelectedIds, id],
          })),
        clearBatchSelection: () => set({ batchSelectedIds: [] }),

        undo: () => {
          const entry = get().globalUndo.at(-1)
          if (entry == null) {
            // Fallback: the selected activity's own persisted history. The
            // step is recorded on the redo stack so Redo reverses exactly it.
            const fallback = fallbackStep('undo')
            if (fallback != null) {
              set((state) => ({ globalRedo: pushCapped(state.globalRedo, fallback) }))
            }
            return
          }
          set((state) => ({ globalUndo: state.globalUndo.slice(0, -1) }))
          const inverse = performAction(entry, 'undo')
          set((state) => ({ globalRedo: pushCapped(state.globalRedo, inverse) }))
        },

        redo: () => {
          const entry = get().globalRedo.at(-1)
          if (entry == null) {
            const fallback = fallbackStep('redo')
            if (fallback != null) {
              set((state) => ({ globalUndo: pushCapped(state.globalUndo, fallback) }))
            }
            return
          }
          set((state) => ({ globalRedo: state.globalRedo.slice(0, -1) }))
          const inverse = performAction(entry, 'redo')
          set((state) => ({ globalUndo: pushCapped(state.globalUndo, inverse) }))
        },

        setTheme: (theme) => set((state) => ({ ui: { ...state.ui, theme } })),

        toggleTheme: () =>
          set((state) => ({
            ui: { ...state.ui, theme: state.ui.theme === 'dark' ? 'light' : 'dark' },
          })),

        setTimeDisplayMode: (timeDisplayMode) =>
          set((state) => ({ ui: { ...state.ui, timeDisplayMode } })),

        setTimelineLayout: (timelineLayout) =>
          set((state) => ({ ui: { ...state.ui, timelineLayout } })),

        setTimelineHeightPx: (heightPx) =>
          set((state) => {
            const { timelineHeightPx: _previous, ...rest } = state.ui
            return {
              ui:
                heightPx != null && Number.isFinite(heightPx)
                  ? { ...rest, timelineHeightPx: Math.round(heightPx) }
                  : rest,
            }
          }),

        setWorkspaceView: (workspaceView) =>
          set((state) => ({ ui: { ...state.ui, workspaceView } })),

        setUnits: (units) => set((state) => ({ ui: { ...state.ui, units } })),

        setMapBasemap: (basemapId) =>
          set((state) => ({
            ui: { ...state.ui, mapLayers: { ...state.ui.mapLayers, basemapId } },
          })),

        setMapOverlay: (overlayId, enabled) =>
          set((state) => {
            const others = state.ui.mapLayers.overlayIds.filter((id) => id !== overlayId)
            return {
              ui: {
                ...state.ui,
                mapLayers: {
                  ...state.ui.mapLayers,
                  overlayIds: enabled ? [...others, overlayId] : others,
                },
              },
            }
          }),

        setMapTerrain3d: (terrain3d) =>
          set((state) => ({
            ui: { ...state.ui, mapLayers: { ...state.ui.mapLayers, terrain3d } },
          })),

        setMapMaximized: (mapMaximized) => set((state) => ({ ui: { ...state.ui, mapMaximized } })),

        setDashboardCollapsed: (dashboardCollapsed) =>
          set((state) => ({ ui: { ...state.ui, dashboardCollapsed } })),

        setHoveredTimestampMs: (timestampMs) => set({ hoveredTimestampMs: timestampMs }),
        setSelectedRangeMs: (range) => set({ selectedRangeMs: range }),

        setMapPickMode: (enabled) =>
          set({
            mapPickMode: enabled,
            ...(enabled
              ? { pickedMapPoint: null, mapEditMode: false, mapEditSelectionMs: [] }
              : {}),
          }),
        setPickedMapPoint: (point) => set({ pickedMapPoint: point, mapPickMode: false }),
        setMapEditMode: (enabled) =>
          set(
            enabled
              ? { mapEditMode: true, mapPickMode: false }
              : { mapEditMode: false, mapEditSelectionMs: [] },
          ),
        setMapEditSelectionMs: (timestampsMs) => set({ mapEditSelectionMs: timestampsMs }),

        setSplitPickMode: (enabled) => set({ splitPickMode: enabled }),
        addSplitPoint: (timestampMs) =>
          set((state) => ({
            splitPointsMs: state.splitPointsMs.includes(timestampMs)
              ? state.splitPointsMs
              : [...state.splitPointsMs, timestampMs].sort((a, b) => a - b),
          })),
        removeSplitPoint: (timestampMs) =>
          set((state) => ({
            splitPointsMs: state.splitPointsMs.filter((t) => t !== timestampMs),
          })),
        clearSplitPoints: () => set({ splitPointsMs: [] }),

        // Shared by `applyOperationToSelected` and `applyOperationToIds`
        // (M7's batch processing) -- applies `operation` to exactly one
        // activity's history, by id, and persists the result. Neither
        // public action duplicates this logic.
        applyOperationToSelected: async (operation, options) => {
          const id = get().selectedActivityId
          if (id == null) return []
          const outcome = await applyOperationToOneId(set, get, id, operation)
          if (outcome.error != null) {
            useNotificationStore.getState().notify({
              variant: 'error',
              title: `${OPERATION_LABELS[operation.type]} failed`,
              description: outcome.error,
            })
            options?.onError?.(outcome.error)
            return null
          }
          const fileName = get().activities.find((a) => a.id === id)?.fileName ?? id
          recordAction({
            kind: 'edit',
            activityIds: [id],
            label: `${OPERATION_LABELS[operation.type]} on ${fileName}`,
          })
          notifyOperationResult(
            operation,
            outcome.warnings,
            options?.successTitle,
            options?.warningsAreNotes,
          )
          return outcome.warnings
        },

        applyOperationToIds: async (ids, operation) => {
          const label = OPERATION_LABELS[operation.type]
          const result: Record<string, OperationWarning[]> = {}
          const failures: { fileName: string; error: string }[] = []
          const succeededIds: string[] = []
          for (const id of ids) {
            const outcome = await applyOperationToOneId(set, get, id, operation)
            if (outcome.error != null) {
              const fileName = get().activities.find((a) => a.id === id)?.fileName ?? id
              failures.push({ fileName, error: outcome.error })
              continue
            }
            result[id] = outcome.warnings
            succeededIds.push(id)
          }

          // One undo step for the whole batch, covering only the ids whose
          // apply actually succeeded (a failed id's history was never touched).
          if (succeededIds.length > 0) {
            recordAction({
              kind: 'edit',
              activityIds: succeededIds,
              label: `Batch ${label.toLowerCase()} (${pluralActivities(succeededIds.length)})`,
            })
          }

          const succeededCount = succeededIds.length
          const totalWarnings = Object.values(result).reduce((sum, w) => sum + w.length, 0)

          if (failures.length > 0) {
            useNotificationStore.getState().notify({
              variant: 'error',
              title:
                succeededCount === 0
                  ? `${label} failed for all ${ids.length} ${ids.length === 1 ? 'activity' : 'activities'}`
                  : `${label} applied to ${succeededCount} of ${ids.length} activities -- ${failures.length} failed`,
              description: failures.map((f) => `${f.fileName}: ${f.error}`).join(' '),
            })
          } else {
            useNotificationStore.getState().notify(
              totalWarnings === 0
                ? {
                    variant: 'success',
                    title: `${label} applied to ${ids.length} ${ids.length === 1 ? 'activity' : 'activities'}`,
                  }
                : {
                    variant: 'warning',
                    title: `${label} applied to ${ids.length} ${ids.length === 1 ? 'activity' : 'activities'} with ${totalWarnings} ${totalWarnings === 1 ? 'note' : 'notes'}`,
                    description: Object.values(result)
                      .flat()
                      .map((w) => w.message)
                      .join(' '),
                  },
            )
          }
          return result
        },

        splitSelectedAtTimestamps: async (atTimestamps) => {
          const loaded = selectSelectedActivity(get())
          if (loaded == null || atTimestamps.length === 0) return []

          let segments: Activity[]
          try {
            const { activity } = await currentActivityAsync(loaded.history)
            segments = await splitActivityAsync(activity, atTimestamps)
          } catch (error) {
            useNotificationStore.getState().notify({
              variant: 'error',
              title: `Could not split "${loaded.fileName}"`,
              description: errorMessage(error),
            })
            return null
          }
          const baseName = loaded.fileName.replace(/\.fit$/i, '')
          const newActivities: LoadedActivity[] = segments.map((segment, i) => ({
            id: createId(),
            fileName: `${baseName} (part ${i + 1} of ${segments.length}).fit`,
            importedAt: Date.now(),
            history: createHistory(segment),
            // Same source file as the parent -- its message layouts (and
            // so its `fieldDefsByMesgNum`) apply unchanged to every
            // segment.
            fieldDefsByMesgNum: loaded.fieldDefsByMesgNum ?? new Map(),
          }))

          appendActivities(newActivities, newActivities[0]?.id ?? get().selectedActivityId)
          if (newActivities.length > 0) {
            recordAction({
              kind: 'add',
              activities: newActivities,
              previousSelectedId: loaded.id,
              label: `Split ${loaded.fileName} into ${newActivities.length} parts`,
            })
          }

          useNotificationStore.getState().notify({
            variant: 'success',
            title: `Split "${loaded.fileName}" into ${segments.length} activities`,
            description: 'Each segment was added to the sidebar as its own independent file.',
          })

          return newActivities.map((a) => a.id)
        },

        hydrateFromPersistence: async () => {
          if (get().activities.length > 0) return
          try {
            const [records, selectedId] = await Promise.all([
              loadAllActivityRecords(),
              loadSelectedActivityId(),
            ])
            if (records.length === 0) return

            const activities: LoadedActivity[] = records.map((record) => ({
              id: record.id,
              fileName: record.fileName,
              importedAt: record.importedAt,
              history: { original: record.original, log: record.log, cursor: record.cursor },
              fieldDefsByMesgNum: fieldDefsEntriesToMap(record.fieldDefsEntries),
            }))

            // Merge, never replace: a file imported while the IndexedDB read
            // was in flight must not be wiped by the restore.
            set((state) => {
              const present = new Set(state.activities.map((a) => a.id))
              const restored = activities.filter((a) => !present.has(a.id))
              const merged = [...restored, ...state.activities]
              return {
                activities: merged,
                selectedActivityId:
                  state.selectedActivityId ??
                  (selectedId != null && merged.some((a) => a.id === selectedId)
                    ? selectedId
                    : merged[merged.length - 1]?.id) ??
                  null,
              }
            })
          } catch (error) {
            useNotificationStore.getState().notify({
              variant: 'warning',
              title: 'Could not restore previously saved activities',
              description: error instanceof Error ? error.message : String(error),
            })
          }
        },

        loadTerrainForSelected: async () => {
          const loaded = selectSelectedActivity(get())
          if (loaded == null) return
          const id = loaded.id
          if (terrainRequests.has(id)) return // already loading

          const requestId = nextTerrainRequestId++
          terrainRequests.set(id, requestId)
          const isCurrent = () =>
            terrainRequests.get(id) === requestId && get().activities.some((a) => a.id === id)
          const patch = (update: (prev: TerrainOverlayState) => TerrainOverlayState) =>
            set((state) => ({
              terrainByActivityId: {
                ...state.terrainByActivityId,
                [id]: update(selectTerrainState(state, id)),
              },
            }))

          patch((prev) => ({
            ...prev,
            status: 'loading',
            progress: { done: 0, total: 0 },
            error: undefined,
          }))

          try {
            const result = await loadTerrainForHistoryAsync(
              requestId,
              loaded.history,
              (progress) => {
                if (isCurrent()) patch((prev) => ({ ...prev, progress }))
              },
            )
            if (!isCurrent()) return
            patch((prev) => ({
              status: 'loaded',
              source: result.source,
              description: result.description,
              summary: result.summary,
              visible: prev.visible,
              revision: prev.revision + 1,
            }))
          } catch (error) {
            if (!isCurrent()) return
            if (isAbortError(error)) {
              // Cancelled: back to whatever was showing before this load.
              patch((prev) => ({
                ...prev,
                status: prev.source != null ? 'loaded' : 'idle',
                progress: undefined,
              }))
              return
            }
            const message = errorMessage(error)
            // Any non-404 failure aborts the whole load -- never a partial overlay.
            patch((prev) => ({
              status: 'error',
              error: message,
              visible: prev.visible,
              revision: prev.revision,
            }))
            useNotificationStore.getState().notify({
              variant: 'error',
              title: 'Could not load terrain elevation',
              description: message,
            })
          } finally {
            if (terrainRequests.get(id) === requestId) terrainRequests.delete(id)
          }
        },

        cancelTerrainLoad: (id) => {
          const requestId = terrainRequests.get(id)
          if (requestId == null) return
          void cancelTerrainLoadAsync(requestId).catch(() => {
            // The worker is gone or the load already finished -- nothing left to cancel.
          })
        },

        setTerrainVisible: (id, visible) =>
          set((state) => {
            const prev = state.terrainByActivityId[id]
            if (prev == null) return {}
            return {
              terrainByActivityId: { ...state.terrainByActivityId, [id]: { ...prev, visible } },
            }
          }),

        removeTerrain: (id) => {
          get().cancelTerrainLoad(id)
          terrainRequests.delete(id)
          set((state) => ({ terrainByActivityId: withoutKey(state.terrainByActivityId, id) }))
        },
      }
    },
    {
      name: 'fit-editor-ui-preferences',
      // Only the UI-preference slice is persisted to localStorage.
      // Decoded activity data never touches localStorage -- it's
      // persisted to IndexedDB instead (M7, see `lib/persistence-db.ts`
      // and `hydrateFromPersistence`), via plain async calls from the
      // actions above rather than this middleware. The terrain overlay
      // (`terrainByActivityId`) is deliberately in neither store: it is
      // session-only, and re-requesting it after a reload is an explicit
      // user action (network access must never happen implicitly). The
      // global undo/redo stacks (`globalUndo`/`globalRedo`) are likewise
      // session-only: after a reload, Undo falls back to each activity's
      // own IndexedDB-persisted edit history.
      partialize: (state) => ({ ui: state.ui }),
      // Deep-merges the persisted `ui` slice over the current defaults, so
      // preferences persisted by an older build (before a field such as
      // `mapLayers` existed) still hydrate to a complete `UiPreferences`
      // rather than replacing it with a partial object.
      merge: (persisted, current) => {
        const persistedUi = (persisted as { ui?: Partial<UiPreferences> } | undefined)?.ui
        return {
          ...current,
          ui: {
            ...current.ui,
            ...persistedUi,
            mapLayers: { ...current.ui.mapLayers, ...persistedUi?.mapLayers },
          },
        }
      },
    },
  ),
)

export function selectSelectedActivity(state: ActivityStoreState): LoadedActivity | undefined {
  return state.activities.find((a) => a.id === state.selectedActivityId)
}

export function selectBatchSelectedActivities(state: ActivityStoreState): LoadedActivity[] {
  return state.activities.filter((a) => state.batchSelectedIds.includes(a.id))
}

/** True if the global undo stack has an entry, or (fallback) the selected activity's own history can undo. */
export function selectCanUndo(state: ActivityStoreState): boolean {
  if (state.globalUndo.length > 0) return true
  const activity = selectSelectedActivity(state)
  return activity != null && fitCanUndo(activity.history)
}

/** True if the global redo stack has an entry, or (fallback) the selected activity's own history can redo. */
export function selectCanRedo(state: ActivityStoreState): boolean {
  if (state.globalRedo.length > 0) return true
  const activity = selectSelectedActivity(state)
  return activity != null && fitCanRedo(activity.history)
}

/** Human label of what Undo would reverse (e.g. "Crop on ride.fit"), or `null` if nothing. */
export function selectUndoLabel(state: ActivityStoreState): string | null {
  const entry = state.globalUndo.at(-1)
  if (entry != null) return entry.label
  return fallbackLabel(selectSelectedActivity(state), 'undo')
}

/** Human label of what Redo would re-apply, or `null` if nothing. */
export function selectRedoLabel(state: ActivityStoreState): string | null {
  const entry = state.globalRedo.at(-1)
  if (entry != null) return entry.label
  return fallbackLabel(selectSelectedActivity(state), 'redo')
}
